import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "theme-preference";

/**
 * Тема — это пользовательская настройка, а не отдельный адрес.
 * Одна и та же страница на одном URL отдаёт одну и ту же разметку,
 * а оформление переключается здесь. Так у контента нет дублей в глазах
 * поисковиков, а ссылку можно безопасно расшаривать.
 */

function readStoredTheme(): Theme | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "dark" || saved === "light") return saved;
  } catch {
    /* приватный режим — игнорируем */
  }
  return null;
}

function readSystemTheme(): Theme {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  } catch {
    return "light";
  }
}

/** Начальное значение: сохранённый выбор → системная тема → светлая */
export function resolveInitialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  return readStoredTheme() ?? readSystemTheme();
}

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Читаем синхронно на первом рендере: без вспышки светлой темы у тех,
  // кто выбрал тёмную.
  const [theme, setThemeState] = useState<Theme>(resolveInitialTheme);
  // Пользователь явно нажал переключатель — значит системную тему больше не слушаем
  const [hasExplicitChoice, setHasExplicitChoice] = useState<boolean>(
    () => typeof window !== "undefined" && readStoredTheme() !== null
  );

  const applyTheme = useCallback((next: Theme) => {
    const root = document.documentElement;
    root.dataset.theme = next;
    root.classList.toggle("dark", next === "dark");
    root.style.colorScheme = next;
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  // Пока выбора не было — следуем за системной темой
  useEffect(() => {
    if (hasExplicitChoice) return;

    let mediaQuery: MediaQueryList;
    try {
      mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    } catch {
      return;
    }

    const handleChange = (event: MediaQueryListEvent) => {
      setThemeState(event.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [hasExplicitChoice]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    setHasExplicitChoice(true);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* приватный режим — тема проживёт до перезагрузки */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, isDark: theme === "dark", setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme должен вызываться внутри ThemeProvider");
  }
  return context;
}

/** Мягкий вариант для компонентов вне провайдера (не должно случаться, но безопасно) */
export function useThemeSafe(): ThemeContextValue {
  return (
    useContext(ThemeContext) ?? {
      theme: "light",
      isDark: false,
      setTheme: () => undefined,
      toggleTheme: () => undefined,
    }
  );
}
